import { Fragment, forwardRef, isValidElement } from "react"
import type {
    BaseSyntheticEvent,
    CSSProperties,
    ForwardRefExoticComponent,
    ForwardedRef,
    KeyboardEvent,
    MouseEvent,
    ReactElement,
    ReactNode
} from "react"

import eq from "lodash-es/eq"
import isArray from "lodash-es/isArray"
import isString from "lodash-es/isString"
import isUndefined from "lodash-es/isUndefined"
import map from "lodash-es/map"

import { ESpinnerVariant } from "@/3514/components/shared/spinner/spinner.types"
import { Typography } from "@/3514/components/shared/typography/typography.component"
import { buildCssClassName } from "@/3514/utils"

import { ESpinnerSize, Spinner } from "../spinner"

import { buttonConfig } from "./button.config"
import { EButtonSize, EButtonVariant } from "./button.types"

type TProps = {
    variant?: EButtonVariant
    size?: EButtonSize
    isBusy?: boolean
    isDisabled?: boolean
    isButtonAccessibleOnDisabled?: boolean // WISER-6582, wtf
    isCentered?: boolean
    isContainer?: boolean
    type?: "button" | "submit" | "reset"
    children?: ReactNode | string
    onClick?(e: MouseEvent): void
    onKeyDown?(e: KeyboardEvent): void
    onFocus?(e: BaseSyntheticEvent): void
    onBlur?(e: BaseSyntheticEvent): void
    onMouseEnter?(e: BaseSyntheticEvent): void
    onMouseLeave?(e: BaseSyntheticEvent): void
    className?: string
    minWidth?: CSSProperties["minWidth"]
    title?: string
    id?: string
    tabIndex?: number
}

const Component: ForwardRefExoticComponent<TProps> = forwardRef<HTMLButtonElement, TProps>(
    (
        {
            className = String(),
            variant = EButtonVariant.Solid,
            size = EButtonSize.Medium,
            isDisabled = false,
            isButtonAccessibleOnDisabled = false,
            isContainer = false,
            isBusy = false,
            isCentered = false,
            minWidth = undefined,
            type = "submit",
            children,
            ...rest
        }: TProps,
        ref: ForwardedRef<HTMLButtonElement>
    ): ReactElement => {
        function renderSpinner(): ReactNode {
            return (
                <div className={buildCssClassName("w-100 h-100", eq(size, EButtonSize.Medium) && "py-[2px]")}>
                    <Spinner
                        variant={eq(variant, EButtonVariant.Solid) ? ESpinnerVariant.White : ESpinnerVariant.Accent}
                        size={ESpinnerSize.Small}
                    />
                </div>
            )
        }

        function renderChildren(): ReactNode {
            if (isString(children))
                return <Typography text={children} {...buttonConfig.getTypographyProps(variant, isDisabled)} />
            if (isArray(children))
                return map<ReactNode>(
                    children,
                    (c: ReactNode, idx: number): ReactNode => (
                        <Fragment key={`btn-child-${idx ** 2}`}>
                            {isValidElement(c) && c}
                            {isString(c) && (
                                <Typography text={c} {...buttonConfig.getTypographyProps(variant, isDisabled)} />
                            )}
                        </Fragment>
                    )
                )
            return children
        }

        return (
            <button
                {...(!!minWidth && { style: { minWidth } })}
                className={buildCssClassName(className, {
                    [buttonConfig.baseClasses]: !isContainer,
                    [buttonConfig.sizeMap[size]()]: size && !isContainer,
                    [buttonConfig.colorMap[variant](isDisabled)]: variant && !isContainer,
                    "[margin-left:auto]": isCentered,
                    "[margin-right:auto]": isCentered,
                    "min-w-[155px]": isUndefined(minWidth) && !isContainer
                })}
                disabled={(isBusy || isDisabled) && !isButtonAccessibleOnDisabled}
                aria-disabled={(isBusy || isDisabled) && !isButtonAccessibleOnDisabled}
                tabIndex={(isBusy || isDisabled) && !isButtonAccessibleOnDisabled ? -1 : 0}
                ref={ref}
                type={type}
                {...rest}
            >
                {isBusy ? renderSpinner() : renderChildren()}
            </button>
        )
    }
)

Component.displayName = "Button"

export { Component as Button, type TProps as TButtonProps }
